import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";
import { toast } from "sonner";
import { API_URL, post } from "../api";

type AcceptInviteResponseData = {
  nextStep?: "login" | "profile_creation" | string;
  token?: string | null;
  email?: string;
  organizationName?: string;
};

const InviteAccept = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token")?.trim() || "";
  }, [location.search]);

  const [status, setStatus] = useState<"loading" | "error" | "done">(
    "loading"
  );
  const [message, setMessage] = useState<string>("Accepting invitation...");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Missing invitation token.");
        return;
      }

      try {
        setStatus("loading");
        setMessage("Accepting invitation...");

        const res = await post(`${API_URL}/partner/organization-invitations/accept`, {
          token,
        });

        const data = (res?.data || {}) as AcceptInviteResponseData;
        const nextStep = data?.nextStep;

        if (cancelled) return;

        if (nextStep === "login") {
          toast.success("Invitation accepted. Please log in to continue.");
          navigate("/login", {
            replace: true,
            state: {
              infoMessage:
                res?.message || "Invitation accepted. Please log in to continue.",
            },
          });
          return;
        }

        if (nextStep === "profile_creation") {
          if (!data?.token) {
            setStatus("error");
            setMessage(
              "Invitation accepted, but we could not start onboarding. Please request a new invitation link."
            );
            return;
          }

          // Reuse the existing onboarding flow which expects a Core JWT in localStorage `token`.
          localStorage.setItem("token", data.token);
          if (data.email) localStorage.setItem("invite_email", data.email);
          if (data.organizationName)
            localStorage.setItem("invite_organization", data.organizationName);

          toast.success("Invitation accepted. Continue onboarding.");
          navigate("/sign-up/create-password", { replace: true });
          return;
        }

        // Fallback
        setStatus("done");
        setMessage(res?.message || "Invitation accepted.");
        navigate("/login", {
          replace: true,
          state: { infoMessage: res?.message || "Invitation accepted." },
        });
      } catch (e: any) {
        if (cancelled) return;
        setStatus("error");
        setMessage(e?.message || "Invalid or expired invitation link.");
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  if (status === "loading") {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center gap-4 p-6">
        <HashLoader />
        <div className="text-gray-700 text-sm">{message}</div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center gap-4 p-6">
        <div className="text-red-600 font-medium">Invitation link error</div>
        <div className="text-gray-700 text-sm text-center max-w-md">
          {message}
        </div>
        <button
          onClick={() => navigate("/login")}
          className="px-4 py-2 rounded-lg bg-deepBlue text-white text-sm"
        >
          Go to login
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col items-center justify-center gap-4 p-6">
      <div className="text-gray-800 font-medium">{message}</div>
    </div>
  );
};

export default InviteAccept;
